import { ChartData } from 'chart.js';
import colors from 'assets/variables/colors';
import { CultureProfile, IJob, IJobCompetencies } from 'core/interface/job';

export const getJobOnlyRadarChart = (
  labels?: string[] | null,
  scores?: number[] | null,
  datasetLabel = 'Job'
): ChartData | null => {
  if (!labels?.length || !scores?.length || labels.length !== scores.length) {
    return null;
  }

  return {
    labels,
    datasets: [
      {
        label: datasetLabel,
        backgroundColor: 'rgba(82, 37, 131, 0.50)',
        borderColor: colors.tenantColor,
        borderWidth: 1,
        data: scores
      }
    ]
  };
};

export const getValidJobCompetencies = (
  competencies?: IJobCompetencies[] | null
): IJobCompetencies[] =>
  (competencies || []).filter((item) => Boolean(item?.competency?.name));

export const getRadarChartFromCompetencies = (
  competencies?: IJobCompetencies[] | null,
  datasetLabel = 'Job'
): ChartData | null => {
  const valid = getValidJobCompetencies(competencies);
  if (!valid.length) {
    return null;
  }

  return getJobOnlyRadarChart(
    valid.map((item) => item.competency.name),
    valid.map((item) => Number(item.min_score)),
    datasetLabel
  );
};

export const getJobCompetenciesChart = (
  job?: IJob | null
): ChartData | null => {
  if (!job) {
    return null;
  }

  return (
    getJobOnlyRadarChart(job.competency_labels, job.competency_min_scores) ||
    getRadarChartFromCompetencies(job.competencies)
  );
};

export const getCultureProfileChart = (
  job?: IJob | null
): ChartData | null => {
  const profile = job?.culture_profile as CultureProfile | null | undefined;
  if (!profile || typeof profile !== 'object') {
    return null;
  }

  const datasetLabel = profile.title || 'Culture';

  return (
    getJobOnlyRadarChart(
      profile.competency_labels,
      profile.competency_min_scores,
      datasetLabel
    ) || getRadarChartFromCompetencies(profile.competencies, datasetLabel)
  );
};

export const getJobChartSignature = (job?: IJob | null): string =>
  JSON.stringify({
    labels: job?.competency_labels || [],
    scores: job?.competency_min_scores || [],
    comps: (job?.competencies || []).map((item) => [
      item?.competency?.id,
      item?.min_score
    ])
  });

export const getCultureChartSignature = (
  profile?: CultureProfile | null
): string =>
  JSON.stringify({
    id: profile?.id ?? null,
    labels: profile?.competency_labels || [],
    scores: profile?.competency_min_scores || [],
    comps: (profile?.competencies || []).map((item) => [
      item?.competency?.id,
      item?.min_score
    ])
  });

export const getCompetencyDescriptions = (
  competencies?: IJobCompetencies[] | null,
  fallbackLabels?: string[]
): string[] => {
  const fromCompetencies = getValidJobCompetencies(competencies).map(
    (item) => item.competency.description || item.competency.name || ''
  );

  if (fromCompetencies.length) {
    return fromCompetencies;
  }

  return fallbackLabels || [];
};
